import * as React from 'react';
import { styled, type StyledComponentWithRef } from '@glitz/react';
import { isSvenssons, Theme, type ThemeType } from '../../theme';
import { createTransitionDecorator } from '@avensia/excite-ui';
import { rippleContainerDecorator, useRipple } from './ripple-effect';
import { createFieldSizeDecorator, FieldSize, useFieldSize, type FieldSizeType } from '../field-size';
import Link from '../Link';
import { createBorderDecorator } from '../border-decorator';
import { createButtonHoverDecorator } from 'webapp/utils/button-hover';

export type PropType = {
  as?: ThemeType;
  disableRippleEffect?: boolean;
  disabled?: boolean;
  size?: FieldSizeType;
  square?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void | Promise<any>;
};

function createButton<TProps, TElement extends HTMLElement>(Component: StyledComponentWithRef<TProps, TElement>) {
  return styled(
    React.forwardRef<TElement, React.PropsWithChildren<PropType> & TProps>(
      ({ children, as = Theme.Primary, disableRippleEffect = false, size, square, disabled, ...restProps }, ref) => {
        const defaultSize = useFieldSize();
        const fieldSize = size ?? defaultSize;

        const [ripples, pushRipple] = useRipple();

        const elementRef = React.useRef<TElement>(null);
        React.useImperativeHandle<TElement | null, TElement | null>(ref, () => elementRef.current);

        React.useEffect(() => {
          const element = elementRef.current;
          if (element && !disableRippleEffect) {
            const click = (e: MouseEvent) => pushRipple(e.currentTarget as TElement, e.clientX, e.clientY);
            element.addEventListener('click', click);

            return () => {
              element.removeEventListener('click', click);
            };
          }
        }, [disableRippleEffect, pushRipple]);

        if (disabled && as !== Theme.Transparent) {
          as = Theme.Disabled;
        }

        const dynamic = React.useMemo(
          () =>
            styled(
              disableRippleEffect ? {} : rippleContainerDecorator,
              createBorderDecorator(as),
              createTransitionDecorator({ property: ['color', 'background', 'opacity', 'border-color'] }),
              createFieldSizeDecorator(fieldSize, square),
              createButtonHoverDecorator({
                color: t => t[as].hoverText ?? t[as].text,
                backgroundColor: t => t[as].hover,
                border: { xy: { color: t => t[as].hoverBorder ?? t[as].hover } },
              }),
              {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                textTransform: t => t.button?.textTransform,
                backgroundColor: t => t[as].color,
                color: t => t[as].text,
                borderRadius: t => t.radius.primary,
                fontWeight: t => t.button?.fontWeight,
              },
            ),
          [as, disableRippleEffect, fieldSize, square],
        );

        const handleClick = React.useCallback(
          (event: React.MouseEvent<HTMLButtonElement>) => {
            if (disabled) {
              return; // Do nothing if the button is disabled
            }
            if (restProps.onClick) {
              restProps.onClick(event); // Execute the onClick function if provided
            }
          },
          [disabled, restProps],
        );

        return (
          <Component
            {...(restProps as any)}
            aria-disabled={disabled}
            onClick={handleClick}
            css={dynamic}
            ref={elementRef}
          >
            {children}
            {ripples}
          </Component>
        );
      },
    ),
  );
}

const resetButtonDecorator = styled(
  {
    font: {
      style: 'inherit',
      variant: 'inherit',
      weight: 'inherit',
    },
    border: { xy: { width: 0 } },
    padding: { xy: 0 },
    userSelect: 'none',
    color: 'inherit',
    backgroundColor: ['transparent', 'initial'],
  },
  isSvenssons && {
    letterSpacing: 0.1,
  },
);

export const Button = createButton(styled.button(resetButtonDecorator));
export const ButtonLink = styled(createButton(Link), {
  // Hack because `Link` has a lot of `color: inherit`
  ':visited': {
    color: undefined,
  },
});

export const ResetButton = styled.button(resetButtonDecorator);
